// FuelEmergencyListener.jsx
// Watches for fuel emergency landings and shows alert modal
// Self-contained: listens for emergencies and renders modal when needed

import { useState, useEffect, useRef } from "react";
import { useWorldState } from "../state/WorldStateContext.jsx";
import FuelEmergencyModal from "../modals/FuelEmergencyModal.jsx";

export default function FuelEmergencyListener() {
  const { worldState } = useWorldState();

  // Track shown emergency IDs to avoid duplicates
  const shownEmergenciesRef = useRef(new Set());

  // Queue of emergencies to show (in case multiple happen at once)
  const [emergencyQueue, setEmergencyQueue] = useState([]);

  // Currently displayed emergency
  const [currentEmergency, setCurrentEmergency] = useState(null);

  // Has first render happened? + startup grace period
  const hasInitializedRef = useRef(false);
  const startupTimeRef = useRef(Date.now());

  useEffect(() => {
    const flights = worldState?.flights || [];

    // FIRST RENDER: Seed the Set with all currently emergency-landed flights
    // These were in emergency state BEFORE we loaded - don't show modal
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      startupTimeRef.current = Date.now();
      flights.forEach((flight) => {
        if (flight && (flight.status === "emergency_landed" || flight.phase === "emergency_landed")) {
          shownEmergenciesRef.current.add(flight.id);
        }
      });
      return; // Don't process anything else on first render
    }

    // GRACE PERIOD: Suppress modals for 10 seconds after startup
    // Scheduled flights may dispatch and fail immediately on load
    if (Date.now() - startupTimeRef.current < 10000) return;

    // SUBSEQUENT RENDERS: Check for new emergency landings
    flights.forEach((flight) => {
      if (!flight) return;

      const isEmergencyLanded = flight.status === "emergency_landed" || flight.phase === "emergency_landed";

      // Skip if already shown
      if (shownEmergenciesRef.current.has(flight.id)) return;

      // Add to queue if flight just had emergency during gameplay
      if (isEmergencyLanded) {
        // Mark as shown
        shownEmergenciesRef.current.add(flight.id);

        // Add to queue
        setEmergencyQueue((prev) => [
          ...prev,
          {
            flight,
            consequences: flight.emergencyLandingConsequences,
          },
        ]);

        console.log(
          "[FuelEmergencyListener] Emergency landing detected:",
          flight.flightNumber,
          flight.emergencyLandingConsequences?.severity
        );
      }
    });

    // Cleanup old shown IDs
    if (shownEmergenciesRef.current.size > 50) {
      const ids = Array.from(shownEmergenciesRef.current);
      shownEmergenciesRef.current = new Set(ids.slice(-25));
    }
  }, [worldState?.flights]);

  // Process queue - show next emergency if none currently displayed
  useEffect(() => {
    if (!currentEmergency && emergencyQueue.length > 0) {
      setCurrentEmergency(emergencyQueue[0]);
      setEmergencyQueue((prev) => prev.slice(1));
    }
  }, [currentEmergency, emergencyQueue]);

  // Handle modal close
  const handleClose = () => {
    setCurrentEmergency(null);
  };

  // Render modal if there's an emergency to show
  if (!currentEmergency) return null;

  return (
    <FuelEmergencyModal
      flight={currentEmergency.flight}
      consequences={currentEmergency.consequences}
      onClose={handleClose}
    />
  );
}
